













































import {mapActions, mapGetters} from 'vuex';
import vue from 'vue';
import {Message, Conversation} from '#/Conversations';
import dayjs from 'dayjs';

interface LocalMessage extends Message {
  isNewDay: boolean;
  newDayTitle: string;
}

export default vue.extend({
  name: 'Detail',

  data: () => ({
    deleteConfirm: false,
    newMessage: '',
    previousUser: null,
    textareaHeight: 0,
  }),

  computed: {
    ...mapGetters([
      'authorProfile',
      'conversation',
    ]),
    userId(): number {
      return parseInt(this.$route.params.userId);
    },
    localMessages(): LocalMessage[] {
      return (this.conversation as Conversation).messages
                                                .sort((a, b) => {
                                                  const aSendAt = dayjs(a.sentAt)
                                                  const bSendAt = dayjs(b.sentAt)

                                                  if (aSendAt.isSameOrAfter(bSendAt)) return 1
                                                  if (bSendAt.isSameOrAfter(aSendAt)) return -1

                                                  return 0;
                                                })
                                                .reduce<{ previousDateMessage: number | null, messages: LocalMessage[] }>(
                                                    (acc, message) => {
                                                      const {sentAt} = message;
                                                      const isNewDay = this.isNewDay(acc.previousDateMessage as number, sentAt);
                                                      acc.previousDateMessage = sentAt.getTime();
                                                      acc.messages.push({
                                                        ...message,
                                                        isNewDay,
                                                        newDayTitle: this.newDayTitle(sentAt),
                                                      });
                                                      return acc;
                                                    },
                                                    {previousDateMessage: null, messages: []},
                                                ).messages;
    },
    hasMessages(): boolean {
      return this.localMessages.length > 0;
    },
  },

  created() {
    if (this.$route.name === 'MessageDetail') {
      this.loadConversation(this.$route.params)
          .then(() => {
            this.loadAuthorProfile({id: this.$route.params.userId})
                .then(() => {
                  this.updatePageTitle(this.authorProfile.name);
                  this.updateAppBarContext({
                    type: 'messageDetail',
                    status: true,
                  });
                });
          });
    } else {
      Promise.all([
        this.loadNewAdminMessages({isDetail: true}),
        this.loadPrivateAdminMessages(true),
      ])
             .then(([ids, adminUserMessageIds]) => {
               (ids as number[]).forEach(id => {
                 this.sendAcknowledgement({id});
               });
               Promise.all((adminUserMessageIds as number[]).map((id) => this.sendUserMessageMarkRead(id)))
                      .then(() => {
                        this.messageNotReadNumber();
                      });
             });
    }
  },

  updated() {
    const list = this.$el.querySelector('#message__list');
    if (list) list.scrollTop = list.scrollHeight;
  },

  methods: {
    ...mapActions([
      'deleteConversation',
      'loadAuthorProfile',
      'loadConversation',
      'loadNewAdminMessages',
      'loadPrivateAdminMessages',
      'messageNotReadNumber',
      'sendAcknowledgement',
      'sendMessage',
      'sendUserMessageMarkRead',
      'updateAppBarContext',
      'updatePageTitle',
    ]),
    isNewDay(previousDate: number, actualDate: Date): boolean {
      return !dayjs(actualDate).isSame(dayjs(previousDate), 'day');
    },
    displayContent(content: string): string {
      return content.replace(/\n/g, '<br/>')
                    .replace(/\s/g, ' ')
                    .replace(
                        /((?:href=")?(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&/=]*)/g,
                        (match): string => {
                          if (match.match('href')) return match;
                          if (match.match('http')) return `<a href="${match}" target="_blank">${match}</a>`;

                          return `<a href="https://${match}" target="_blank">${match}</a>`;
                        },
                    );
    },
    displayHour(date: Date): string {
      return dayjs(date).format('LT');
    },
    displayReadDate(date: Date): string {
      return dayjs(date).format('LLLL');
    },
    messageLocation(index: number): 'alone__message' | 'first__message' | 'last__message' | 'between__message' | undefined {
      const previousMessage: LocalMessage | undefined = this.localMessages[(index - 1)];
      const prevId = previousMessage?.author.id;
      const message: LocalMessage | undefined = this.localMessages[index];
      const id = message?.author.id;
      const nextMessage: LocalMessage | undefined = this.localMessages[(index + 1)];
      const nextId = nextMessage?.author.id;

      if (message.isNewDay && (nextMessage === undefined || nextId !== id)) {
        return 'alone__message';
      } else if (message.isNewDay && (nextMessage !== undefined || nextId === id)) {
        return 'first__message';
      }

      if ((previousMessage === undefined || prevId !== id) && (nextMessage === undefined || nextMessage.isNewDay)) {
        return 'alone__message';
      } else if ((previousMessage === undefined || prevId === id) && (nextMessage === undefined || nextMessage.isNewDay)) {
        return 'last__message';
      }

      if ((previousMessage === undefined || prevId !== id) && (nextMessage === undefined || nextId !== id)) {
        return 'alone__message';
      } else if ((previousMessage === undefined || prevId !== id) && (nextMessage !== undefined || nextId === id)) {
        return 'first__message';
      } else if ((previousMessage !== undefined || prevId === id) && (nextMessage !== undefined || nextId === id)) {
        return 'between__message';
      } else if ((previousMessage !== undefined || prevId === id) && (nextMessage === undefined || nextId !== id)) {
        return 'last__message';
      }
    },
    messageStatus(message: LocalMessage): 'sent' | 'read' {
      return message.readAt === null ? 'sent' : 'read';
    },
    newDayTitle(date: Date): string {
      return dayjs(date).format('l');
    },
    /**
     * On récupère la taille minimal du textarea quand il est vide.
     */
    onTextareaFocus(): void {
      const textarea = this.$el.querySelector('#message__text_area') as HTMLTextAreaElement;
      const savedValue = textarea.value;
      textarea.value = '';
      this.textareaHeight = textarea.scrollHeight;
      textarea.value = savedValue;
    },
    onTextareaInput(): void {
      const textarea = this.$el.querySelector('#message__text_area') as HTMLTextAreaElement;
      let minRows = parseInt(textarea.getAttribute('data-min-rows') || '0');
      let rows = 0;
      textarea.rows = minRows;
      rows = Math.ceil((textarea.scrollHeight - this.textareaHeight) / 16);
      textarea.rows = minRows + rows > 6 ? 6 : minRows + rows;
    },
    onSendNewMessage(): void {
      const {advertId: advert, userId: recipient} = this.$route.params;

      this.sendMessage({
        advert,
        recipient,
        content: this.newMessage,
      })
          .then(() => {
            this.newMessage = '';
            const textarea = this.$el.querySelector('#message__text_area') as HTMLTextAreaElement;
            textarea.rows = parseInt(textarea.getAttribute('data-min-rows') || '0');
          });
    },
  },
});
